/**
 * Minimal mock for the `ws` package used by server-only code paths.
 */

import { EventEmitter } from './events.js';

export type WebSocketServerOptions = {
  host?: string;
  port?: number;
  path?: string;
};

export class MockWebSocket extends EventEmitter {
  static CONNECTING = 0;
  static OPEN = 1;
  static CLOSING = 2;
  static CLOSED = 3;

  readyState = MockWebSocket.OPEN;
  url?: string;

  constructor(url?: string) {
    super();
    this.url = url;
  }

  send(_data: unknown): void {
    // no-op
  }

  close(): void {
    if (this.readyState === MockWebSocket.CLOSED) return;
    this.readyState = MockWebSocket.CLOSING;
    queueMicrotask(() => {
      this.readyState = MockWebSocket.CLOSED;
      this.emit('close');
    });
  }

  terminate(): void {
    this.close();
  }

  ping(): void {
    // no-op
  }

  pong(): void {
    // no-op
  }

  isAlive(): boolean {
    return this.readyState === MockWebSocket.OPEN;
  }
}

export class MockWebSocketServer extends EventEmitter {
  clients: Set<MockWebSocket> = new Set();

  constructor(public readonly options: WebSocketServerOptions = {}) {
    super();
  }

  address(): { port: number; address: string } {
    return {
      port: this.options.port ?? 0,
      address: this.options.host ?? '0.0.0.0',
    };
  }

  handleUpgrade(_request: unknown, _socket: unknown, _head: unknown, callback: (ws: MockWebSocket) => void): void {
    const ws = new MockWebSocket();
    this.clients.add(ws);
    callback(ws);
  }

  close(cb?: () => void): void {
    for (const client of this.clients) {
      client.close();
    }
    this.emit('close');
    cb?.();
  }
}

export { MockWebSocket as WebSocket, MockWebSocketServer as WebSocketServer };

export default {
  WebSocket: MockWebSocket,
  WebSocketServer: MockWebSocketServer,
};
